import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookOpen, faCheckCircle, faLock } from '@fortawesome/free-solid-svg-icons';
import MetaComponent from '../../../components/common/MetaComponent';
import { fetchMaterials } from '../../../services/materials.util';
import {
  getMaterialPurchaseInfo,
  createMaterialPurchaseOrder,
  purchaseMaterial
} from '../../../services/materialsPurchase.util';
import { initializeRazorpayPayment } from '../../../services/razorpay.util';
import { useAuthContext } from '../../../context/Context';
import { useMessageBox } from '../../../components/others/MessageBox';
import AOS from 'aos';

const RENEWAL_MESSAGE = 'Your subscription has ended. Please renew it to continue accessing this content.';

const MaterialsListPage = () => {
  const { loggedIn, loggedInUser } = useAuthContext();
  const { showMessage } = useMessageBox();
  const [searchParams] = useSearchParams();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchaseInfo, setPurchaseInfo] = useState({});
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const selectedMaterialSlug = searchParams.get('material');
  const displayedMaterials = selectedMaterialSlug
    ? materials.filter((material) => material.slug === selectedMaterialSlug)
    : materials;
  const selectedMaterial = selectedMaterialSlug ? displayedMaterials[0] : null;

  useEffect(() => {
    const loadMaterials = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchMaterials();
        setMaterials(data || []);
      } catch (err) {
        setError(err.message || 'Failed to load materials');
        setMaterials([]);
      } finally {
        setLoading(false);
      }
    };
    loadMaterials();
  }, []);

  useEffect(() => {
    const loadPurchaseInfo = async () => {
      if (!loggedIn || materials.length === 0) return;
      try {
        setPurchaseLoading(true);
        const purchaseData = {};
        await Promise.all(
          materials.map(async (material) => {
            try {
              const info = await getMaterialPurchaseInfo(material._id);
              purchaseData[material._id] = info;
            } catch (err) {
              purchaseData[material._id] = {
                material: { ...material, isPurchasable: true },
                userStatus: { hasPurchased: false, canAccess: false }
              };
            }
          })
        );
        setPurchaseInfo(purchaseData);
      } catch (err) {
        console.error('Error loading purchase info:', err);
      } finally {
        setPurchaseLoading(false);
      }
    };
    loadPurchaseInfo();
  }, [materials, loggedIn]);

  // Refresh AOS animations when data is loaded
  useEffect(() => {
    if (!loading && !purchaseLoading && displayedMaterials.length > 0) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        AOS.refresh();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, purchaseLoading, displayedMaterials.length]);

  const handlePaymentSuccess = useCallback(
    async (response) => {
      try {
        showMessage('Verifying payment...', 'info');
        const purchaseResponse = await purchaseMaterial(response.id, {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature
        });
        if (purchaseResponse.success) {
          showMessage('Payment successful! Material purchased.', 'success');
          const data = await fetchMaterials();
          setMaterials(data || []);
        } else {
          throw new Error(purchaseResponse.message || 'Purchase verification failed');
        }
      } catch (err) {
        showMessage(err.message || 'Payment verification failed', 'error');
      } finally {
        setProcessingPayment(false);
      }
    },
    [showMessage]
  );

  useEffect(() => {
    const handleMessage = async (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'RAZORPAY_SUCCESS' && data.data?.id) {
          await handlePaymentSuccess(data.data);
        } else if (data.type === 'RAZORPAY_ERROR') {
          showMessage(
            data.error?.description || data.error?.message || 'Payment failed',
            'error'
          );
          setProcessingPayment(false);
        }
      } catch (e) {
        // Ignore non-JSON messages - this is expected for non-Razorpay messages
        if (process.env.NODE_ENV === 'development') {
          // Only log in development to avoid console noise
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handlePaymentSuccess, showMessage]);

  const handlePurchaseClick = async (material) => {
    if (!loggedInUser) {
      showMessage('Please login to purchase materials', 'error');
      return;
    }
    if (processingPayment) {
      showMessage('Payment is already being processed', 'warning');
      return;
    }
    try {
      setProcessingPayment(true);
      const orderResponse = await createMaterialPurchaseOrder(material._id);
      if (!orderResponse.success) {
        throw new Error(orderResponse.message || 'Failed to create payment order');
      }
      const { order, razorpayKeyId, callbackUrl } = orderResponse.data;
      showMessage('Creating payment order...', 'info');
      const options = {
        key: razorpayKeyId,
        amount: order.amount,
        currency: order.currency,
        name: 'gvkEDU',
        description: `Purchase ${material.title}`,
        order_id: order.id,
        callback_url: callbackUrl,
        redirect: true,
        prefill: {
          contact: loggedInUser.mobileNumber || '',
          email: loggedInUser.email || ''
        },
        notes: {
          id: material._id,
          materialTitle: material.title
        },
        theme: { color: '#000000' },
        modal: {
          ondismiss: function () {
            setProcessingPayment(false);
            showMessage('Payment cancelled', 'warning');
          }
        }
      };
      // Initialize Razorpay payment using utility
      await initializeRazorpayPayment(options, (error) => {
        throw error;
      });
    } catch (err) {
      showMessage(err.message || 'Failed to initiate payment', 'error');
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div className="container-fluid px-4 py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" />
            <p className="text-white mt-3">Loading materials...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-content">
        <div className="container-fluid px-4 py-5">
          <div className="text-center text-white">
            <p>{error}</p>
            <button className="btn btn-primary" onClick={() => window.location.reload()}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <style>{`
        .coursesCard {
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid rgba(124, 58, 237, 0.5);
          border-radius: 28px;
          overflow: hidden;
          background: linear-gradient(135deg, 
            rgba(0, 0, 0, 0.85) 0%, 
            rgba(20, 20, 20, 0.8) 50%, 
            rgba(0, 0, 0, 0.85) 100%
          );
          backdrop-filter: blur(25px) saturate(180%);
          -webkit-backdrop-filter: blur(25px) saturate(180%);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 
                      0 0 0 1px rgba(255, 255, 255, 0.1),
                      inset 0 1px 0 rgba(255, 255, 255, 0.1);
          position: relative;
          cursor: pointer;
          will-change: transform, box-shadow;
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        
        .coursesCard::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
        }
        
        .coursesCard::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.1) 0%, 
            rgba(255, 255, 255, 0.05) 50%, 
            rgba(255, 255, 255, 0.1) 100%
          );
          opacity: 0;
          transition: opacity 0.4s ease;
          pointer-events: none;
        }
        
        .coursesCard:hover {
          transform: translateY(-20px) scale(1.03);
          box-shadow: 0 50px 100px rgba(0, 0, 0, 0.6), 
                      0 0 80px rgba(124, 58, 237, 0.3), 
                      0 0 0 2px rgba(124, 58, 237, 0.6),
                      inset 0 1px 0 rgba(255, 255, 255, 0.2);
          border-color: rgba(124, 58, 237, 0.7);
        }
        
        .coursesCard:hover::after {
          opacity: 1;
        }
        
        .coursesCard__content {
          color: rgba(255, 255, 255, 0.85) !important;
          padding: 20px 30px 20px 30px !important;
        }
        
        .coursesCard__content .coursesCard__text {
          color: rgba(255, 255, 255, 0.7) !important;
          margin-bottom: 0 !important;
          line-height: 1.5 !important;
        }
        
        .exam-features-list,
        .material-features-list {
          padding: 0;
          margin-top: 15px;
        }
        
        .tier-feature {
          display: flex;
          align-items: flex-start;
          margin-bottom: 10px;
          font-size: 14px !important;
          color: rgba(255, 255, 255, 0.75) !important;
          line-height: 1.5;
        }
        
        .tier-feature-icon {
          color: #10b981 !important;
          margin-right: 10px;
          font-size: 12px !important;
          margin-top: 2px;
          flex-shrink: 0;
        }
        
        .coursesCard__title {
          color: white !important;
        }
        
        .text-light-1 {
          color: rgba(255, 255, 255, 0.6) !important;
        }
        
        .coursesCard__footer {
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          background: rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
          padding: 20px 30px 25px 30px !important;
        }
        
        .coursesCard__footer .text-14 {
          color: rgba(255, 255, 255, 0.9) !important;
          font-weight: 600 !important;
        }
        
        .coursesCard__footer .badge {
          font-size: 8px !important;
          font-weight: 700 !important;
          letter-spacing: 0.3px;
          text-transform: uppercase;
          animation: pulse 2s infinite;
          padding: 3px 6px !important;
        }
        
        .coursesCard__footer .badge.bg-success {
          background: linear-gradient(135deg, #10b981, #059669) !important;
          color: white !important;
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);
          font-size: 10px !important;
          padding: 4px 8px !important;
          font-weight: 700 !important;
        }
        
        .coursesCard__footer .badge.bg-warning {
          background: linear-gradient(135deg, #f59e0b, #d97706) !important;
          color: white !important;
          box-shadow: 0 2px 8px rgba(245, 158, 11, 0.4);
          font-size: 10px !important;
          padding: 4px 8px !important;
          font-weight: 700 !important;
        }
        
        @keyframes pulse {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0.8;
          }
          100% {
            opacity: 1;
          }
        }
        
        .coursesCard__header {
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, rgba(31, 41, 55, 0.9) 0%, rgba(17, 24, 39, 0.95) 100%);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border-radius: 20px 20px 0 0;
          padding: 30px !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .coursesCard__header::before {
          content: '';
          position: absolute;
          top: 20px;
          right: 30px;
          width: 60px;
          height: 60px;
          background: radial-gradient(circle, rgba(124, 58, 237, 0.25) 0%, transparent 70%);
          border-radius: 50%;
          border: 1px solid rgba(124, 58, 237, 0.4);
          backdrop-filter: blur(0px);
        }
        
        .coursesCard__header::after {
          content: '';
          position: absolute;
          top: 40px;
          right: 60px;
          width: 40px;
          height: 40px;
          background: radial-gradient(circle, rgba(124, 58, 237, 0.2) 0%, transparent 70%);
          border-radius: 50%;
          border: 1px solid rgba(124, 58, 237, 0.3);
          backdrop-filter: blur(0px);
        }
        
        .coursesCard__header .coursesCard__icon {
          background: rgba(124, 58, 237, 0.2) !important;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(124, 58, 237, 0.5) !important;
          color: #A78BFA !important;
          box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);
        }
        
        .coursesCard__header .coursesCard__title {
          color: white !important;
        }
        
        .coursesCard__header .text-light-1 {
          color: rgba(255, 255, 255, 0.7) !important;
        }
        
        .shine-effect {
          position: absolute;
          top: 0;
          left: -120%;
          width: 120%;
          height: 100%;
          background: linear-gradient(
            110deg, 
            transparent 0%, 
            rgba(255, 255, 255, 0.1) 45%,
            rgba(255, 255, 255, 0.3) 50%, 
            rgba(255, 255, 255, 0.1) 55%,
            transparent 100%
          );
          transition: left 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          pointer-events: none;
          border-radius: 28px;
          transform: skewX(-15deg);
          opacity: 0;
        }

        .coursesCard:hover .shine-effect {
          left: 120% !important;
          opacity: 1;
        }
        
        .row.y-gap-30 {
          display: flex !important;
          flex-wrap: wrap !important;
          margin-left: -10px !important;
          margin-right: -10px !important;
        }
        
        .row.y-gap-30 > div[class*="col-"] {
          display: flex !important;
        }
        
        .row.y-gap-30 > div[class*="col-"] > .text-decoration-none {
          display: flex !important;
          width: 100%;
        }
        
        .row.y-gap-30 .coursesCard {
          display: flex !important;
          flex-direction: column !important;
          height: 100% !important;
          width: 100%;
        }
        
        @media (max-width: 991px) {
          .row.y-gap-30 {
            display: block !important;
          }
          
          .row.y-gap-30 > div[class*="col-"] {
            display: block !important;
          }
          
          .row.y-gap-30 > div[class*="col-"] > .text-decoration-none {
            display: block !important;
          }
          
          .row.y-gap-30 .coursesCard {
            height: auto !important;
          }
        }
        
        .main-content {
          background: #000000;
          min-height: 100vh;
          position: relative;
        }
        
        .main-content::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICAgIDxnIGZpbGw9Im5vbmUiIGZpbGwtcnVsZT0iZXZlbm9kZCI+CiAgICAgICAgPGcgZmlsbD0icmdiYSgyNTUsIDI1NSwgMjU1LCAwLjAzKSIgZmlsbC1ydWxlPSJub256ZXJvIj4KICAgICAgICAgICAgPGNpcmNsZSBjeD0iNyIgY3k9IjciIHI9IjciLz4KICAgICAgICA8L2c+CiAgICA8L2c+Cjwvc3ZnPg==');
          opacity: 0.4;
          pointer-events: none;
        }
        
        .content-wrapper {
          position: relative;
          z-index: 1;
        }
        
        .page-header {
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
        }
        
        .page-header__title {
          color: white !important;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }
        
        .page-header__text {
          color: rgba(255, 255, 255, 0.8) !important;
        }
        
        .simple-purchase-btn {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px;
          padding: 16px 32px !important;
          margin: 0 20px 20px 20px !important;
          width: calc(100% - 40px) !important;
          position: relative;
          overflow: hidden !important;
          border-radius: 12px !important;
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.15) 0%, 
            rgba(255, 255, 255, 0.10) 100%
          ) !important;
          border: 1.5px solid rgba(255, 255, 255, 0.3) !important;
          backdrop-filter: blur(20px) !important;
          -webkit-backdrop-filter: blur(20px) !important;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3),
                      inset 0 1px 0 rgba(255, 255, 255, 0.2) !important;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
          font-size: 15px !important;
          font-weight: 600 !important;
          letter-spacing: 0.3px !important;
          color: rgba(255, 255, 255, 0.95) !important;
          text-transform: none !important;
        }
        
        .simple-purchase-btn:hover {
          transform: translateY(-2px) !important;
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.22) 0%, 
            rgba(255, 255, 255, 0.15) 100%
          ) !important;
          border: 1.5px solid rgba(255, 255, 255, 0.5) !important;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4),
                      inset 0 1px 0 rgba(255, 255, 255, 0.3),
                      0 0 20px rgba(255, 255, 255, 0.15) !important;
        }
        
        .simple-purchase-btn:active {
          transform: translateY(0px) !important;
        }
        
        .pricing-tiers-container {
          padding: 20px 20px 25px 20px;
          background: rgba(0, 0, 0, 0.3);
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
          margin-top: auto;
        }
        
        .price-display-section {
          padding: 20px 20px 10px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .price-info-left {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        
        .price-main-row {
          display: flex;
          align-items: baseline;
          gap: 12px;
        }
        
        .price-current {
          font-size: 24px !important;
          font-weight: 700 !important;
          color: #ffffff !important;
          line-height: 1 !important;
          text-shadow: 0 2px 12px rgba(255, 255, 255, 0.3);
        }
        
        .price-original {
          font-size: 16px !important;
          color: rgba(255, 255, 255, 0.4) !important;
          text-decoration: line-through !important;
          font-weight: 500 !important;
        }
        
        .price-savings {
          font-size: 12px !important;
          color: #10b981 !important;
          font-weight: 600 !important;
        }
        
        .price-discount-badge {
          display: inline-block;
          font-size: 11px !important;
          font-weight: 700 !important;
          color: #10b981 !important;
          background: rgba(16, 185, 129, 0.15) !important;
          padding: 4px 10px !important;
          border-radius: 6px !important;
          letter-spacing: 0.5px !important;
          border: 1px solid rgba(16, 185, 129, 0.3) !important;
        }
        
        .purchased-indicator {
          background: rgba(16, 185, 129, 0.2) !important;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          color: rgba(255, 255, 255, 0.95) !important;
          padding: 12px 20px !important;
          border-radius: 12px !important;
          font-weight: 600 !important;
          margin: 0 20px 20px 20px;
          text-align: center;
          font-size: 14px !important;
          border: 1px solid rgba(16, 185, 129, 0.3);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
          position: relative;
          overflow: hidden;
        }
        
        .purchased-indicator::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
        }
        
        .tier-purchase-btn.purchased {
          background: linear-gradient(135deg, 
            rgba(0, 0, 0, 0.8) 0%, 
            rgba(15, 15, 15, 0.7) 50%,
            rgba(0, 0, 0, 0.8) 100%
          ) !important;
          color: #ffffff !important;
          border: 2px solid rgba(255, 255, 255, 0.3) !important;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          padding: 18px 36px !important;
          margin: 0 20px 20px 20px !important;
          width: calc(100% - 40px) !important;
          min-height: 52px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          text-decoration: none !important;
          border-radius: 50px !important;
          font-size: 15px !important;
          font-weight: 700 !important;
          letter-spacing: 0.8px !important;
          text-transform: uppercase !important;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
          backdrop-filter: blur(15px) !important;
          -webkit-backdrop-filter: blur(15px) !important;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5),
                      0 0 20px rgba(255, 255, 255, 0.2),
                      inset 0 2px 0 rgba(255, 255, 255, 0.15),
                      inset 0 -1px 0 rgba(0, 0, 0, 0.2) !important;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
          will-change: transform, box-shadow;
        }

        .tier-purchase-btn.purchased::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
        }

        .tier-purchase-btn.purchased:hover {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.15) 0%, 
            rgba(0, 0, 0, 0.9) 50%,
            rgba(255, 255, 255, 0.1) 100%
          ) !important;
          color: #ffffff !important;
          transform: translateY(-6px) scale(1.08);
          box-shadow: 0 25px 60px rgba(0, 0, 0, 0.6), 
                      0 0 50px rgba(255, 255, 255, 0.3),
                      inset 0 2px 0 rgba(255, 255, 255, 0.25),
                      inset 0 -2px 0 rgba(0, 0, 0, 0.3) !important;
          border: 2px solid rgba(255, 255, 255, 0.5) !important;
          text-decoration: none !important;
        }

        .tier-purchase-btn.purchased:active {
          transform: translateY(-4px) scale(1.05);
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5), 
                      0 0 40px rgba(255, 255, 255, 0.25) !important;
        }

        .tier-purchase-btn.purchased:focus {
          text-decoration: none !important;
          outline: none;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5),
                      0 0 20px rgba(255, 255, 255, 0.2),
                      0 0 0 3px rgba(255, 255, 255, 0.3) !important;
        }
        
        .tier-purchase-btn .button-shine {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg, 
            transparent 0%, 
            rgba(255, 255, 255, 0.2) 45%,
            rgba(255, 255, 255, 0.4) 50%, 
            rgba(255, 255, 255, 0.2) 55%,
            transparent 100%
          );
          transition: left 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          pointer-events: none;
          border-radius: 50px;
          transform: skewX(-20deg);
          opacity: 0;
        }

        .tier-purchase-btn:hover .button-shine {
          left: 100% !important;
          opacity: 1;
        }
        
        @keyframes cardFloat {
          0%, 100% { 
            transform: translateY(0px);
          }
          50% { 
            transform: translateY(-5px);
          }
        }
        
        .coursesCard {
          animation: cardFloat 6s ease-in-out infinite;
        }

        .coursesCard:nth-child(2n) {
          animation-delay: 2s;
        }

        .coursesCard:nth-child(3n) {
          animation-delay: 4s;
        }
        
        @media (max-width: 768px) {
          .coursesCard__content {
            padding: 0 20px 15px 20px !important;
          }
          
          .coursesCard__footer {
            padding: 12px 20px 15px 20px !important;
          }
          
          .coursesCard__footer .text-14 {
            font-size: 12px !important;
          }
          
          .price-display-section {
            padding: 18px 15px 8px 15px;
          }
          
          .price-current {
            font-size: 22px !important;
          }
          
          .price-original {
            font-size: 14px !important;
          }
          
          .simple-purchase-btn {
            padding: 14px 28px !important;
            margin: 0 15px 15px 15px !important;
            width: calc(100% - 30px) !important;
            font-size: 14px !important;
          }
          
          .tier-purchase-btn.purchased {
            padding: 14px 28px !important;
            font-size: 13px !important;
          }
        }
      `}</style>
      <MetaComponent
        title="Study Materials || GVK Student Portal"
        description="Access comprehensive study materials and PDFs for your preparation."
      />
      <div className="content-wrapper js-content-wrapper overflow-hidden">
        {/* Page Header */}
        <section className="page-header">
          <div className="container">
            <div className="row justify-center text-center">
              <div className="col-xl-6 col-lg-8">
                <h1 className="page-header__title">
                  {selectedMaterialSlug ? selectedMaterial?.title || 'Selected Material' : 'Study Materials'}
                </h1>
                <p className="page-header__text">
                  {selectedMaterialSlug
                    ? 'Showing only the material pack selected from the home carousel.'
                    : 'Purchase material packs and access PDFs by subject.'}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Materials Grid Section */}
        <section className="layout-pt-md layout-pb-md">
          <div className="container">
            {selectedMaterialSlug && (
              <div className="row mb-4">
                <div className="col-12">
                  <div
                    className="alert alert-info d-flex align-items-center justify-content-between"
                    style={{
                      background: 'rgba(124, 58, 237, 0.15)',
                      backdropFilter: 'blur(15px)',
                      WebkitBackdropFilter: 'blur(15px)',
                      border: '1px solid rgba(124, 58, 237, 0.3)',
                      borderRadius: '12px',
                      padding: '1rem 1.5rem',
                      color: 'rgba(255, 255, 255, 0.95)',
                    }}
                  >
                    <div>
                      <strong>Viewing Selected Material</strong>
                      <span className="ms-2">
                        - Only {selectedMaterial?.title || 'the selected material'} is shown.
                      </span>
                    </div>
                    <Link
                      to="/materials"
                      className="btn btn-sm"
                      style={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        color: 'white',
                        padding: '0.5rem 1rem',
                        borderRadius: '8px',
                        textDecoration: 'none',
                      }}
                    >
                      View All Materials
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Materials Grid */}
            {!loading && !error && displayedMaterials.length > 0 && (
              <div className="row y-gap-30">
                {displayedMaterials.map((material, index) => {
                  const info = purchaseInfo[material._id];
                  const userStatus = info?.userStatus || { hasPurchased: false, canAccess: false };
                  const materialInfo = info?.material || material;
                  const isFree = materialInfo.isFree || !materialInfo.isPurchasable;
                  const hasAccess = userStatus.canAccess || isFree || loggedInUser?.role === 'admin';
                  const isExpired = userStatus.isExpired;
                  const effectivePrice =
                    materialInfo.discountPrice ?? materialInfo.price ?? 0;

                  return (
                    <div
                      key={material._id}
                      className="col-lg-4 col-md-6 px-20 py-20"
                    >
                      <Link
                        to={`/materials/${material.slug}`}
                        className="text-decoration-none"
                      >
                        <div
                          className="coursesCard -type-1 rounded-12 bg-dark shadow-3 h-100 transition-all duration-300"
                          data-aos="fade-up"
                          data-aos-duration="600"
                          data-aos-delay={index * 100}
                          data-aos-offset="50"
                          data-aos-once="true"
                        >
                          {/* Shine Effect */}
                          <div className="shine-effect"></div>
                          
                          {/* Card Header */}
                          <div className="coursesCard__header p-30 pb-20">
                            <div className="d-flex align-items-center mb-20">
                              <div 
                                className="coursesCard__icon size-50 rounded-full d-flex align-items-center justify-content-center mr-15"
                                style={{
                                  background: 'rgba(124, 58, 237, 0.2)',
                                  borderColor: 'rgba(124, 58, 237, 0.5)',
                                  color: '#A78BFA'
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faBookOpen}
                                  size="lg"
                                />
                              </div>
                              <div className="flex-grow-1">
                                <h4 className="coursesCard__title text-18 lh-1 fw-500 mb-1">
                                  {material.title}
                                </h4>
                                <span className="text-14 text-light-1">
                                  Study Material Pack
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Card Content */}
                          <div className="coursesCard__content">
                            <p className="coursesCard__text text-14 lh-18 text-light-1 mb-10">
                              {material.description ||
                                'Access comprehensive study materials and PDFs for your preparation.'}
                            </p>
                            {isExpired && (
                              <p className="text-warning text-13 lh-18 mb-10">
                                {userStatus.message || RENEWAL_MESSAGE}
                              </p>
                            )}

                            {/* Display material features under description */}
                            {material.features && material.features.length > 0 && (
                              <div className="material-features-list mt-15">
                                {material.features.map((feature, featureIndex) => (
                                  <div
                                    key={featureIndex}
                                    className="tier-feature mb-8"
                                  >
                                    <FontAwesomeIcon
                                      icon={faCheckCircle}
                                      className="tier-feature-icon"
                                    />
                                    <span>{feature}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Purchase/Pricing Section */}
                          {hasAccess ? (
                            <div className="pricing-tiers-container">
                              <Link
                                to={`/materials/${material.slug}`}
                                className="tier-purchase-btn full-exam-btn simple-purchase-btn purchased"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="button-shine"></div>
                                Go to Learning
                                <span style={{ marginLeft: '4px' }}>
                                  →
                                </span>
                              </Link>
                            </div>
                          ) : (
                            <div className="pricing-tiers-container">
                              {effectivePrice > 0 ? (
                                <>
                                  {/* Price Display */}
                                  <div className="price-display-section">
                                    <div className="price-info-left">
                                      <div className="price-main-row">
                                        <span className="price-current">
                                          ₹{effectivePrice}
                                        </span>
                                        {materialInfo.discountPrice && (
                                          <span className="price-original">
                                            ₹{materialInfo.price}
                                          </span>
                                        )}
                                      </div>
                                      {materialInfo.discountPrice && (
                                        <span className="price-savings">
                                          You save ₹{materialInfo.price - effectivePrice}
                                        </span>
                                      )}
                                    </div>
                                    {materialInfo.discountPrice && (
                                      <span className="price-discount-badge">
                                        {Math.round((1 - effectivePrice / materialInfo.price) * 100)}% OFF
                                      </span>
                                    )}
                                  </div>

                                  {/* Purchase Button */}
                                  <button
                                    className={`simple-purchase-btn ${processingPayment ? 'processing' : ''}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handlePurchaseClick(material);
                                    }}
                                    disabled={processingPayment}
                                  >
                                    {processingPayment ? (
                                      <>
                                        <div
                                          className="spinner-border spinner-border-sm me-2"
                                          role="status"
                                        >
                                          <span className="visually-hidden">Loading...</span>
                                        </div>
                                        Processing...
                                      </>
                                    ) : (
                                      <>
                                        {isExpired ? 'Renew Access' : 'Buy Now'}
                                        <span style={{ marginLeft: '4px' }}>→</span>
                                      </>
                                    )}
                                  </button>
                                </>
                              ) : (
                                <div className="purchased-indicator">
                                  Not Available for Purchase
                                </div>
                              )}
                            </div>
                          )}

                          {/* Card Footer */}
                          <div className="coursesCard__footer -auto">
                            <div className="d-flex align-items-center justify-between">
                              <span className="text-14 fw-600">
                                {hasAccess ? (
                                  <>
                                    Start Learning
                                    <span style={{ marginLeft: '8px', fontSize: '14px' }}>→</span>
                                  </>
                                ) : (
                                  <>
                                    <FontAwesomeIcon icon={faLock} className="me-2" />
                                    View Details
                                  </>
                                )}
                              </span>
                              <div className="d-flex align-items-center">
                                {hasAccess ? (
                                  <span className="badge bg-success bg-opacity-20 text-success px-8 py-3 text-10 fw-600 rounded-pill">
                                    UNLOCKED
                                  </span>
                                ) : (
                                  <span className="badge bg-warning bg-opacity-20 text-warning px-8 py-3 text-10 fw-600 rounded-pill">
                                    LOCKED
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && displayedMaterials.length === 0 && (
              <div className="row justify-center">
                <div className="col-lg-6">
                  <div className="text-center py-60">
                    <FontAwesomeIcon
                      icon={faBookOpen}
                      size="3x"
                      className="text-light-3 mb-20"
                    />
                    <h5 className="mb-10">
                      {selectedMaterialSlug ? 'Selected Material Not Found' : 'No Materials Available'}
                    </h5>
                    <p className="text-light-1">
                      {selectedMaterialSlug
                        ? 'The material selected from the carousel is no longer available.'
                        : 'There are currently no materials available. Please check back later.'}
                    </p>
                    {selectedMaterialSlug && (
                      <Link
                        to="/materials"
                        className="button -md -outline-purple-1 text-purple-1 mt-20"
                      >
                        View All Materials
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default MaterialsListPage;
